import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  Icon,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import EditarContaAPagar from "layouts/payables/components/editData";
import { findBillingStatus } from "layouts/payables/components/yupSchema";
import EditarContaAReceber from "layouts/receivables/components/editData";
import { useEffect, useState } from "react";
import { exportToExcel } from "react-json-to-excel";
import FinanceiroService from "services/financeiro";
import { ptBR_DataGrid } from "utils/datagrid";
import { dataCalendario } from "utils/dataCalendario";

const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(v) || 0
  );

const COM_BAIXA = [
  "baixadoAMais",
  "baixadaEmAberto",
  "canceladaComBaixa",
  "baixaSemConta",
];

/**
 * Divergências do financeiro: contas e saldos que fazem os números não
 * baterem. Corrigir daqui (botão de editar) antes de fechar um período.
 */
export default function Divergencias() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [selecionada, setSelecionada] = useState(null);
  const [editarPagar, setEditarPagar] = useState(null);
  const [editarReceber, setEditarReceber] = useState(null);

  const buscar = async () => {
    setLoading(true);
    setErro("");
    try {
      const r = await FinanceiroService.divergencias();
      setDados(r);
      setSelecionada(
        (atual) =>
          atual || r.resumo.find((x) => x.quantidade > 0)?.chave || null
      );
    } catch (e) {
      setErro(e?.error || e?.message || "Erro ao levantar as divergências.");
    }
    setLoading(false);
  };

  useEffect(() => {
    buscar();
  }, []);

  const verificacao = dados?.resumo.find((r) => r.chave === selecionada);
  const linhas = (selecionada && dados?.itens[selecionada]) || [];
  const total = dados?.resumo.reduce((acc, r) => acc + r.quantidade, 0) || 0;

  const colunas = [
    { field: "tipo", headerName: "Tipo", minWidth: 100 },
    { field: "descricao", headerName: "Descrição", flex: 1.4 },
    { field: "pessoa", headerName: "Cliente / Fornecedor", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      valueGetter: ({ row }) =>
        row.status ? findBillingStatus(row.status) : "",
    },
    {
      field: "dataVencimento",
      headerName: "Vencimento",
      minWidth: 105,
      valueGetter: ({ row }) => dataCalendario(row.dataVencimento),
    },
    {
      field: "dataPagamento",
      headerName:
        selecionada === "saldoDivergente" || selecionada === "extratoPendente"
          ? "Data"
          : "Pagamento",
      minWidth: 105,
      valueGetter: ({ row }) => dataCalendario(row.dataPagamento),
    },
    {
      field: "valor",
      headerName: selecionada === "saldoDivergente" ? "Informado" : "Valor",
      minWidth: 120,
      align: "right",
      headerAlign: "right",
      valueGetter: ({ row }) => brl(row.valor),
    },
    ...(COM_BAIXA.includes(selecionada)
      ? [
          {
            field: "baixado",
            headerName: "Baixado",
            minWidth: 120,
            align: "right",
            headerAlign: "right",
            valueGetter: ({ row }) =>
              row.baixado !== undefined ? brl(row.baixado) : "",
          },
          { field: "lotes", headerName: "Lotes", flex: 0.8 },
        ]
      : []),
    ...(selecionada === "saldoDivergente"
      ? [
          {
            field: "calculado",
            headerName: "Calculado",
            minWidth: 120,
            align: "right",
            headerAlign: "right",
            valueGetter: ({ row }) => brl(row.calculado),
          },
          {
            field: "diferenca",
            headerName: "Diferença",
            minWidth: 120,
            align: "right",
            headerAlign: "right",
            valueGetter: ({ row }) => brl(row.diferenca),
          },
        ]
      : []),
    {
      field: "acoes",
      headerName: "",
      minWidth: 60,
      sortable: false,
      renderCell: ({ row }) =>
        row.contaId ? (
          <MDButton
            variant="gradient"
            color="info"
            iconOnly
            onClick={() =>
              row.contaTipo === "payables"
                ? setEditarPagar(row.contaId)
                : setEditarReceber(row.contaId)
            }
          >
            <Icon fontSize="small">edit</Icon>
          </MDButton>
        ) : null,
    },
  ];

  const exportar = () =>
    exportToExcel(
      dados.resumo
        .filter((r) => r.quantidade > 0)
        .map((r) => ({
          sheetName: r.titulo.slice(0, 31),
          details: dados.itens[r.chave].map((l) => ({
            Tipo: l.tipo,
            Descrição: l.descricao,
            "Cliente/Fornecedor": l.pessoa || "",
            Status: l.status ? findBillingStatus(l.status) : "",
            Vencimento: dataCalendario(l.dataVencimento),
            Data: dataCalendario(l.dataPagamento),
            Valor: l.valor,
            Baixado: l.baixado ?? "",
            Lotes: l.lotes || "",
            Calculado: l.calculado ?? "",
            Diferença: l.diferenca ?? "",
          })),
        })),
      "divergencias_financeiro",
      true
    );

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        {editarPagar && (
          <EditarContaAPagar
            setShowEditState={() => setEditarPagar(null)}
            currentIdAction={editarPagar}
            fetchData={buscar}
          />
        )}
        {editarReceber && (
          <EditarContaAReceber
            setShowEditState={() => setEditarReceber(null)}
            currentIdAction={editarReceber}
            fetchData={buscar}
          />
        )}
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <MDTypography variant="h6" color="white" mt={1}>
              Divergências do financeiro
            </MDTypography>
            <Box display="flex" gap={1}>
              <MDButton variant="contained" onClick={buscar} disabled={loading}>
                {loading ? "Verificando..." : "Verificar de novo"}
              </MDButton>
              <MDButton
                variant="contained"
                onClick={exportar}
                disabled={!total}
              >
                Exportar
              </MDButton>
            </Box>
          </MDBox>
          <MDBox p={3}>
            {erro && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {erro}
              </Alert>
            )}
            {dados && total === 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Nenhuma divergência encontrada.
              </Alert>
            )}
            <Grid container spacing={2}>
              {dados?.resumo.map((r) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={r.chave}>
                  <Card
                    onClick={() => setSelecionada(r.chave)}
                    sx={{
                      p: 2,
                      height: "100%",
                      cursor: "pointer",
                      border: "2px solid",
                      borderColor:
                        selecionada === r.chave ? "info.main" : "transparent",
                      opacity: r.quantidade ? 1 : 0.55,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {r.titulo}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        color: r.quantidade ? "error.dark" : "text.primary",
                      }}
                    >
                      {r.quantidade}
                    </Typography>
                    {r.quantidade > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {brl(r.valor)}
                      </Typography>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>

            {verificacao && (
              <Box mt={4}>
                <Typography variant="h6">{verificacao.titulo}</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {verificacao.descricao}
                </Typography>
                <DataGrid
                  rows={linhas}
                  columns={colunas}
                  autoHeight
                  density="compact"
                  loading={loading}
                  localeText={ptBR_DataGrid}
                  rowSelection={false}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 50 } },
                  }}
                  pageSizeOptions={[50, 100]}
                />
                {linhas.length >= 500 && (
                  <Typography variant="caption" color="text.secondary">
                    Mostrando as primeiras 500. Corrija e clique em
                    &quot;Verificar de novo&quot;.
                  </Typography>
                )}
              </Box>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
